import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  ScrollView,
  Center,
  StatusBar,
  Stack,
  Pressable as NBPressable,
  Spinner,
} from "native-base";
import { LinearGradient } from "expo-linear-gradient";
import { Pressable } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import axios from "axios";
import { useSelector } from "react-redux";
import { GlobalStyles } from "../../constants/styles";
import { GlobalConstants } from "../../constants/constants";



const BASE_URL = `${GlobalConstants.SurveyBaseUrl}`.replace(/\/$/, "");

const ROW_HEIGHT = 40;
const BUTTON_WIDTH = 140;

const GradientButton = ({ children, onPress, disabled }) => (
  <Box
    borderRadius={10}
    overflow="hidden"
    borderWidth={1}
    borderColor={GlobalStyles.colors.black}
    bg={GlobalStyles.colors.white}
    w={["100%", "100%", `${BUTTON_WIDTH}px`]}
    opacity={disabled ? 0.5 : 1}
  >
    <LinearGradient
      colors={[
        GlobalStyles.colors.gradientBlueStart ?? "#000561",
        GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{
        width: "100%",
        height: 44,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 10,
      }}
    >
      <Pressable
        onPress={disabled ? undefined : onPress}
        disabled={disabled}
        style={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Text
          color={GlobalStyles.colors.mustard}
          fontWeight="500"
          fontSize="16px"
          letterSpacing="0.1px"
          lineHeight="20px"
          fontFamily={GlobalStyles.text.fontFamily}
        >
          {children}
        </Text>
      </Pressable>
    </LinearGradient>
  </Box>
);

const InteractiveStarRow = ({ value = 0, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const stars = [1, 2, 3, 4, 5];

  return (
    <HStack space={2} alignItems="center" justifyContent="flex-end">
      {stars.map((s) => {
        const filled = s <= (hovered || value);
        return (
          <Pressable
            key={s}
            onPress={() => onChange(s)}
            onMouseEnter={() => setHovered(s)}
            onMouseLeave={() => setHovered(0)}
            hitSlop={4}
          >
            <MaterialIcons
              name={filled ? "star" : "star-border"}
              size={28}
              color={
                filled
                  ? GlobalStyles.colors.mustard
                  : GlobalStyles.colors.black
              }
            />
          </Pressable>
        );
      })}
    </HStack>
  );
};

const SurveyQuestionRow = ({ text, stars, onStarsChange }) => (
  <HStack
    w="100%"
    alignItems="center"
    justifyContent="space-between"
    mb={2}
    style={{ minHeight: ROW_HEIGHT }}
  >
    <Box
      flex={1}
      borderWidth={1}
      borderColor={GlobalStyles.colors.black}
      borderRadius={8}
      bg={GlobalStyles.colors.white}
      px={4}
      h={`${ROW_HEIGHT}px`}
      justifyContent="center"
      mr={3}
    >
      <Text
        color={GlobalStyles.colors.black}
        fontSize="14px"
        fontWeight="400"
        fontFamily={GlobalStyles.text.fontFamily}
        numberOfLines={1}
      >
        {text}
      </Text>
    </Box>

    <InteractiveStarRow value={stars} onChange={onStarsChange} />
  </HStack>
);

const ThankYouView = ({ onRateAgain }) => (
  <VStack space={4} alignItems="center" py={8}>
    <MaterialIcons
      name="check-circle"
      size={64}
      color={GlobalStyles.colors.gradientBlueEnd ?? "#000AC7"}
    />
    <Text
      fontSize="22px"
      fontWeight="600"
      color={GlobalStyles.colors.black}
      fontFamily={GlobalStyles.text.fontFamily}
      textAlign="center"
    >
      Thank you for your rating!
    </Text>
    <Text
      fontSize="14px"
      color={GlobalStyles.colors.black}
      fontFamily={GlobalStyles.text.fontFamily}
      textAlign="center"
      opacity={0.7}
    >
      Your feedback helps us improve our services.
    </Text>

    <HStack
      mt={4}
      w="100%"
      justifyContent="space-between"
      space={[4, 4, 3]}
      flexDirection={["column", "column", "row"]}
    >
      <GradientButton onPress={onRateAgain}>Rate Again</GradientButton>
    </HStack>
  </VStack>
);

const CitizenSurvey = ({ onBack }) => {
  const user = useSelector((state) => state.user?.user);
  const userId = String(user.id);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [ratings, setRatings] = useState({});

  const cardWidthProp = ["92%", "85%", 650];

  const loadSurvey = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSubmitted(false);
    setRatings({});

    try {
      const surveysRes = await axios.get(`${BASE_URL}/api/survey/public`);
      const surveys = surveysRes.data?.surveys ?? surveysRes.data ?? [];

      if (!surveys || surveys.length === 0) {
        setError("No surveys available at the moment.");
        setLoading(false);
        return;
      }

      let latest = surveys[surveys.length - 1];
      if (surveys[0]?.createdAt) {
        latest = surveys.reduce((a, b) =>
          new Date(a.createdAt) > new Date(b.createdAt) ? a : b,
        );
      }
      setSurvey(latest);

      const questionsRes = await axios.get(
        `${BASE_URL}/api/question/public/${latest.id}`,
      );
      const qs =
        questionsRes.data?.questions ??
        questionsRes.data ?? [];

      setQuestions(qs);
    } catch (err) {
      console.error("CitizenSurvey load error:", err);
      setError("Failed to load survey. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSurvey();
  }, [loadSurvey]);

  const handleSubmit = async () => {
    const unrated = questions.filter((q) => !ratings[q.id]);
    if (unrated.length > 0) {
      setError(`Please rate all ${questions.length} question(s) before submitting.`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const promises = questions.map((q) =>
        axios.post(`${BASE_URL}/api/rating/public`, {
          question_id: q.id,
          user_id: userId,
          rating: ratings[q.id],
        }),
      );
      await Promise.all(promises);
      setSubmitted(true);
    } catch (err) {
      console.error("CitizenSurvey submit error:", err);
      console.error("Server response:", err.response?.data);
      setError("Failed to submit ratings. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRateAgain = () => {
    loadSurvey();
  };

  return (
    <>
      <StatusBar
        backgroundColor={GlobalStyles.colors.blue}
        barStyle="light-content"
      />
      <ScrollView flex={1} bg="muted.100">
        <Center>
          <Stack
            mt={4}
            w={cardWidthProp}
            alignItems="center"
            justifyContent="center"
          >
            {onBack && (
              <HStack w="100%" mb={3} px={[2, 4, 4]}>
                <NBPressable onPress={onBack}>
                  <HStack alignItems="center" space={2}>
                    <Text
                      color={GlobalStyles.colors.blue}
                      fontWeight="500"
                      fontSize={[13, 14, 14]}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      ← Back to Services
                    </Text>
                  </HStack>
                </NBPressable>
              </HStack>
            )}

            <Box
              w="100%"
              bg={GlobalStyles.colors.white}
              borderRadius={12}
              p={[4, 5, 6]}
              mb={6}
            >
              {loading && (
                <Center py={10}>
                  <Spinner
                    size="lg"
                    color={GlobalStyles.colors.gradientBlueEnd ?? "#000AC7"}
                  />
                  <Text
                    mt={4}
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize="14px"
                  >
                    Loading survey…
                  </Text>
                </Center>
              )}

              {!loading && error && !submitted && (
                <VStack space={4} alignItems="center" py={6}>
                  <MaterialIcons
                    name="error-outline"
                    size={48}
                    color={GlobalStyles.colors.red ?? "#D32F2F"}
                  />
                  <Text
                    color={GlobalStyles.colors.red ?? "#D32F2F"}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize="14px"
                    textAlign="center"
                  >
                    {error}
                  </Text>
                  <GradientButton onPress={loadSurvey}>Retry</GradientButton>
                </VStack>
              )}

              {!loading && submitted && (
                <ThankYouView onRateAgain={handleRateAgain} />
              )}

              {!loading && !submitted && survey && (
                <VStack space={2}>
                  <Box
                    w="100%"
                    borderWidth={1}
                    borderColor={GlobalStyles.colors.black}
                    borderRadius={8}
                    bg={GlobalStyles.colors.white}
                    px={4}
                    h={`${ROW_HEIGHT}px`}
                    justifyContent="center"
                    mb={2}
                  >
                    <Text
                      color={GlobalStyles.colors.black}
                      fontSize="16px"
                      fontWeight="500"
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {survey.name}
                    </Text>
                  </Box>

                  {questions.length === 0 && (
                    <Text
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize="14px"
                      opacity={0.6}
                      py={4}
                      textAlign="center"
                    >
                      No questions found for this survey.
                    </Text>
                  )}

                  {questions.map((q) => (
                    <SurveyQuestionRow
                      key={q.id}
                      text={q.text}
                      stars={ratings[q.id] ?? 0}
                      onStarsChange={(val) =>
                        setRatings((prev) => ({ ...prev, [q.id]: val }))
                      }
                    />
                  ))}

                  {error && (
                    <Text
                      color={GlobalStyles.colors.red ?? "#D32F2F"}
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize="12px"
                      mt={1}
                    >
                      {error}
                    </Text>
                  )}

                  {questions.length > 0 && (
                    <Box mt={4} w="100%">
                      <HStack
                        justifyContent="flex-end"
                        w="100%"
                        space={[4, 4, 3]}
                        flexDirection={["column", "column", "row"]}
                      >
                        <GradientButton
                          onPress={handleSubmit}
                          disabled={submitting}
                        >
                          {submitting ? "Submitting…" : "Rate the Survey"}
                        </GradientButton>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>
          </Stack>
        </Center>
      </ScrollView>
    </>
  );
};

export default CitizenSurvey;